<template>
  <q-input-frame
    class="q-chips-input"
    :focused="focused"
    :length="length"
    :additional-length="input.length > 0"
    @click="__onClick"
  >
    <div class="col row items-center group q-input-chips">
      <q-chip
        small
        closable
        v-for="(customer, index) in model"
        :key="customer.id"
        @hide="remove(index)"
        text-color="grey-8"
      >
        {{ getCustomerName(customer) }}
      </q-chip>

      <input
        ref="input"
        class="col q-input-target"
        v-model="input"
        :placeholder="placeholder"
        v-bind="$attrs"
        @focus="__onFocus"
        @blur="__onInputBlur"
        @keydown="__handleKeyDown"
        @keyup="__onKeyup"
      />
    </div>

    <q-spinner
      v-if="isLoading"
      slot="after"
      size="24px"
      class="q-if-control"
    ></q-spinner>

    <infinite-autocomplete
      ref="autocomplete"
      @fetch="handleInfiniteScroll"
      @search="handleSearch"
      @selected="handleSelected"
      :debounce="defaultSearchDebounce"
      :min-characters="0"
      :max-results="200"
      :max-height="'300px'"
    />
  </q-input-frame>
</template>

<script>
  import { getCustomerName, mapCustomer } from 'src/utils/searchResultParser'
  import { CustomersClient } from 'src/api/Customers'
  import InfiniteAutocomplete from 'components/Common/InfiniteAutocomplete.vue'
  import { QChipsInput } from 'quasar'
  import { businessPartnerTypes } from 'src/utils/businessPartnerTypes'
  import { customerTypes } from 'src/utils/customerTypes'
  import debounceMixin from 'src/utils/mixins/debounce'
  import i18n from 'src/i18n/i18n'

  export default {
    mixins: [debounceMixin, QChipsInput],

    props: {
      value: {
        type: Array,
        required: true
      },

      partnerType: {
        type: Array,
        default: () => [businessPartnerTypes.CUSTOMER, businessPartnerTypes.CUSTOMER_AND_SUPPLIER]
      },

      types: {
        required: false,
        default: () => [customerTypes.COMPANY, customerTypes.PERSON],
        type: Array
      },

      placeholder: {
        type: String,
        default: () => i18n.tc('customer', 2)
      }
    },

    data () {
      return {
        input: '',
        model: this.value.slice(),
        watcher: null,
        shadow: {
          val: this.input,
          set: this.add,
          loading: false,
          selectionOpen: false,
          watched: 0,
          isDark: () => this.dark,
          hasFocus: () => document.activeElement === this.$refs.input,
          register: () => {
            this.shadow.watched += 1
            this.__watcherRegister()
          },
          unregister: () => {
            this.shadow.watched = Math.max(0, this.shadow.watched - 1)
            this.__watcherUnregister()
          },
          getEl: () => this.$refs.input
        }
      }
    },

    watch: {
      value (v) {
        this.model = v.slice()
      }
    },

    provide () {
      return {
        __input: this.shadow
      }
    },

    computed: {
      length () {
        return this.model
          ? this.model.length
          : 0
      },

      isLoading () {
        return this.loading || (this.shadow.watched && this.shadow.loading)
      },

      filters () {
        return [
          ['business_partner_type', '=', [businessPartnerTypes.CUSTOMER, businessPartnerTypes.CUSTOMER_AND_SUPPLIER]],
          ['type', '=', this.types]
        ]
      }
    },

    methods: {
      getCustomerName,

      handleSelected () {
        this.$nextTick(() => {
          this.$refs.autocomplete.trigger()
        })
      },

      add (value = this.input) {
        clearTimeout(this.timer)
        this.focus()

        if (this.isLoading || !this.editable || !value) {
          return
        }
        if (this.model.includes(value)) {
          this.$emit('duplicate', value)
          return
        }

        this.model.push(value)
        this.$emit('input', this.model)
        this.input = ''
      },

      remove (index) {
        clearTimeout(this.timer)
        this.focus()
        if (this.editable && index >= 0 && index < this.length) {
          this.model.splice(index, 1)
          this.$emit('input', this.model)
        }
      },

      handleSearch (query, done) {
        this.executeSearch(query)
          .then(response => {
            const isLastPage = response.data.meta.pagination.current_page === response.data.meta.pagination.total_pages

            done(response.data.data.map(mapCustomer), isLastPage)
          })
      },

      executeSearch (term) {
        const client = new CustomersClient()
          .filter(this.filters)
          .include(['addresses'])

        if (!term) {
          return client.sort('name:asc')
            .list()
        }

        return client.search(term)
      },

      handleInfiniteScroll (query, index, done) {
        if (query) {
          done([], true)
          return
        }

        new CustomersClient()
          .filter(this.filters)
          .include(['addresses'])
          .sort('name:asc')
          .page(index + 1)
          .list()
          .then(response => {
            const isLastPage = response.data.meta.pagination.current_page === response.data.meta.pagination.total_pages

            return done(response.data.data.map(mapCustomer), isLastPage)
          })
      },

      __handleKeyDown (e) {
        switch (this.__getEventKey(e)) {
        case 8: // Backspace key
          if (!this.input.length && this.length) {
            this.remove(this.length - 1)
          }
          return
        default:
          return this.__onKeydown(e)
        }
      },

      __getEventKey (e) {
        return e.which || e.keyCode
      }
    },

    components: {
      InfiniteAutocomplete
    }
  }
</script>
